import React, { ReactElement, useContext } from 'react';
import {
  Box,
  Button as ChakraButton,
  Flex,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { ProgressCircle } from '@allergan-data-labs/alle-elements-progress-circle';
import {
  ButtonProps,
  ButtonSizes,
  mapButtonSizeToProgressCircleSize,
  mapButtonColorSchemeToProgressColorScheme,
} from './button';
import { generateTextButtonTheme, getIconStyles } from './text-button.theme';
import { ButtonGroupContext } from './button-context';

export type TextButtonColorScheme =
  | 'action'
  | 'brand'
  | 'negative'
  | 'positive';
export interface TextButtonProps extends ButtonProps {
  children?: React.ReactNode;
  /** The visual color appearance of the TextButton */
  colorScheme?: TextButtonColorScheme;
  /** If `true`, the button will be disabled */
  isDisabled?: boolean;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
  /** The size of the IconButton */
  size?: ButtonSizes;
  /**
   * If `true`, the button will show a spinner and will be disabled.
   * @default false
   */
  isLoading?: boolean;
  /** Positions an icon on the left side of the text button */
  leftIcon?: ReactElement | undefined;
  /** Positions an icon on the right side of the text button */
  rightIcon?: ReactElement | undefined;
}

/**
 * Buttons are commonly used in software and websites as a way for users to interact with the system and perform actions such as submitting a form,
 * navigating to another page, or executing a command.
 *
 * Text buttons are typically used for less important actions.
 */
export const TextButton = forwardRef<TextButtonProps, 'button'>(
  (
    {
      leftIcon,
      rightIcon,
      children,
      isLoading,
      isDisabled,
      colorMode: propColorMode,
      colorScheme = 'action',
      size = 'md',
      ...props
    },
    ref
  ) => {
    const { colorMode: contextColorMode } = useColorMode();
    const buttonGroupProps = useContext(ButtonGroupContext);
    const colorMode =
      propColorMode || buttonGroupProps?.colorMode || contextColorMode;

    const styles = generateTextButtonTheme(colorScheme, colorMode, size);

    if (leftIcon) {
      styles.paddingLeft = '0';
    }

    if (rightIcon) {
      styles.paddingRight = '0';
    }
    const visibility = isLoading ? 'hidden' : 'visible';

    return (
      <ChakraButton
        ref={ref}
        __css={styles}
        isDisabled={isDisabled || isLoading}
        {...props}
      >
        <Flex visibility={visibility}>
          {leftIcon && <Box sx={getIconStyles('left', size)}>{leftIcon}</Box>}
          {children}
          {rightIcon && (
            <Box sx={getIconStyles('right', size)}>{rightIcon}</Box>
          )}
        </Flex>
        {isLoading && (
          <Box position="absolute">
            <ProgressCircle
              colorScheme={mapButtonColorSchemeToProgressColorScheme(
                colorScheme
              )}
              colorMode={colorMode}
              size={mapButtonSizeToProgressCircleSize(size)}
            />
          </Box>
        )}
      </ChakraButton>
    );
  }
);
